import * as dataAccess from 'app/utils/ajax';
import { API_CONSTANTS } from 'app/constants/api-contants';
import { fetchData } from './comonsearch';

export const getCustomerList = async (param) => {
  const baseUrl = `${API_CONSTANTS.CUSTOMER}/list`;
  let responseObj = await fetchData(baseUrl, param);
  return responseObj;
};

export const updateCustomerStatus = async (id, status) => {
  let url = '';
  url = `${API_CONSTANTS.CUSTOMER}/update-customer?id=${id}`;

  let payload = {
    data: {
      status: status,
    },
  };
  let response = await dataAccess.patch(url, payload);
  return response;
};

export const exportCustomers = async (params) => {
  const baseUrl = `${API_CONSTANTS.CUSTOMER}/export`;
  return await fetchData(baseUrl, params);
};

export const getCustomerInfo = async (id) => {
  let responseObj = await dataAccess.get(
    `${API_CONSTANTS.CUSTOMER}/detail?id=${id}`
  );
  return responseObj;
};

// Uploads the customer's government ID document to the private bucket and
// returns its S3 key. Store the key — the detail endpoint returns a separate
// presigned URL for display, and presigned URLs expire.
export async function uploadGovtIdDocument(file) {
  let formData = new FormData();
  formData.append('image', file);
  let payload = {
    data: formData,
  };

  let responseObj = await dataAccess.upload(
    `${API_CONSTANTS.CUSTOMER}/govt-id-upload`,
    payload
  );

  return responseObj;
}

export const updateCustomerInfo = async (id, data) => {
  let url = '';
  url = `${API_CONSTANTS.CUSTOMER}/update-customer?id=${id}`;
  let payload = {
    data: {
      ...data,
    },
  };
  let response = await dataAccess.patch(url, payload);
  return response;
};

// Appends ?bucket=before|after along with whichever boundary dates are known.
// The backend uses restorationDate as the "before" upper bound when present
// so that gap-period rows (deletion <= ts < restoration) stay in "Before",
// matching the chosen split-by-restoration-date design.
const appendBucketParams = (url, bucket, deletionDate, restorationDate) => {
  if (bucket !== 'before' && bucket !== 'after') return url;
  let next = `${url}&bucket=${bucket}`;
  if (deletionDate) {
    next += `&deletionDate=${encodeURIComponent(deletionDate)}`;
  }
  if (restorationDate) {
    next += `&restorationDate=${encodeURIComponent(restorationDate)}`;
  }
  return next;
};

export const getCustomerBillHistory = async (
  id,
  page,
  dataPerPage,
  bucket,
  deletionDate,
  restorationDate
) => {
  const base = `${API_CONSTANTS.CUSTOMER}/bill?id=${id}&page=${page}&limit=${dataPerPage}`;
  const url = appendBucketParams(base, bucket, deletionDate, restorationDate);
  let responseObj = await dataAccess.get(url);
  return responseObj;
};

// Triggers the async Bill History Excel export. Returns { jobId }; poll via
// getExportStatus until done, then download the url (same flow as reports).
export const exportCustomerBillHistory = async (
  id,
  downloadType,
  bucket,
  deletionDate,
  restorationDate
) => {
  const base = `${API_CONSTANTS.CUSTOMER}/bill?id=${id}&download=true&downloadType=${
    downloadType || 'excel'
  }`;
  const url = appendBucketParams(base, bucket, deletionDate, restorationDate);
  return await dataAccess.get(url);
};

// URL moved to the Phase 3 mixed-row endpoint (P3 D-15: plain vouchers plus
// package rows). The name and signature are kept on purpose so the explicit
// three-function mock factory in CustomerVouchers/useCustomerVouchers.test.js
// stays valid.
export const getCustomerVouchers = async (
  id,
  page,
  dataPerPage,
  bucket,
  deletionDate,
  restorationDate
) => {
  const base = `${API_CONSTANTS.CUSTOMER}/package/voucher-list?id=${id}&page=${page}&limit=${dataPerPage}`;
  const url = appendBucketParams(base, bucket, deletionDate, restorationDate);
  let responseObj = await dataAccess.get(url);
  return responseObj;
};

// Triggers the async per-customer voucher export (Customer Detail → Vouchers
// tab). Returns { jobId }; poll via getExportStatus until done, then hand the
// url to triggerDownload — same flow as exportCustomerBillHistory above.
//
// Deliberately sends no bucket params: the export always covers the customer's
// full voucher set regardless of which on-screen bucket table is showing,
// matching how the Bill History export passes bucket = null.
export const exportCustomerVouchers = async (id, downloadType) => {
  const url = `${API_CONSTANTS.CUSTOMER}/voucher-export?id=${id}&downloadType=${
    downloadType || 'excel'
  }`;
  return await dataAccess.get(url);
};

export const updateVoucherDetails = async ({
  phoneNumber,
  voucherCode,
  serialNumber,
  status,
  voucherId
}) => {
  let url = '';
  url = `${API_CONSTANTS.CUSTOMER}/voucher-details?phoneNumber=${phoneNumber}&voucherCode=${voucherCode}`;

  let payload = {
    data: {
      status: status,
      serialNumber: serialNumber,
      voucherId: voucherId,
    },
  };
  let response = await dataAccess.put(url, payload);
  return response;
};

export const getCustomerMallWiseTier = async (
  id,
  page,
  dataPerPage,
  bucket,
  deletionDate,
  restorationDate
) => {
  const base = `${API_CONSTANTS.CUSTOMER}/mall-tier?id=${id}&page=${page}&limit=${dataPerPage}`;
  const url = appendBucketParams(base, bucket, deletionDate, restorationDate);
  let responseObj = await dataAccess.get(url);
  return responseObj;
};

// URL moved to the Phase 3 mixed-row endpoint (P3 D-15); name kept to preserve
// the existing mock factories.
export const getVouchersByPhone = async (phone, page, dataPerPage) => {
  let responseObj = await dataAccess.get(
    `${API_CONSTANTS.CUSTOMER}/package/voucher-list?phone=${phone}&page=${page}&limit=${dataPerPage}`
  );
  return responseObj;
};

export const getCustomerLedger = async (
  id,
  page,
  limit,
  bucket,
  deletionDate,
  restorationDate
) => {
  const base = `${API_CONSTANTS.CUSTOMER}/ledger-details?id=${id}&page=${page}&limit=${limit}`;
  const url = appendBucketParams(base, bucket, deletionDate, restorationDate);
  let responseObj = await dataAccess.get(url);
  return responseObj;
};

export const getCustomerPrivilege = async (id, page, dataPerPage, bucket) => {
  // Privileges use a dedicated deleted/restored view: bucket=before (Account
  // deleted, read-only history) / bucket=after (Account restored, collectable
  // with reset counts). The backend resolves the deletion/Influencer dates
  // itself, so only the bucket flag is sent.
  let url = `${API_CONSTANTS.CUSTOMER}/privilege-history?id=${id}&page=${page}&limit=${dataPerPage}`;
  if (bucket === 'before' || bucket === 'after') {
    url += `&bucket=${bucket}`;
  }
  let responseObj = await dataAccess.get(url);
  return responseObj;
};

export const updateCustomerPrivilege = async ({
  phoneNumber,
  privilegeId,
  mallId,
}) => {
  let url = '';
  url = `${API_CONSTANTS.CUSTOMER}/collect-privilege`;

  let payload = {
    data: {
      phone: phoneNumber,
      privilegeId: privilegeId,
      mallId: mallId,
    },
  };
  let response = await dataAccess.post(url, payload);
  return response;
};

export const restoreCustomerStatus = async (id) => {
  let url = '';
  url = `${API_CONSTANTS.CUSTOMER}/restore-customer?id=${id}`;

  let response = await dataAccess.patch(url);
  return response;
};
